import {ButtonGroup, Checkbox} from '@kit';

import React from 'react';

import {ItemFilterInput, ItemRarity, ItemSubType, ItemType, SourceBook} from '../../../generated/types';
import {enumList, Filters, FiltersPopup, FilterValues, flag} from '../../../components/filters';
import {ItemSubTypeNames} from '../../../components/render-helpers';
import {itemRarityToString, itemTypesToString, sourceBookToString} from '../../../components/enums';
import {toUpperFirst} from '../../../components/utils';

import formCss from '../../../components/form.module.css';

const WONDROUS = 'Wondrous';

const BOOK_TYPES: Array<ItemType | typeof WONDROUS> = [
	ItemType.Weapon,
	ItemType.Armor,
	ItemType.Ring,
	ItemType.Rod,
	ItemType.Staff,
	ItemType.Wand,
	WONDROUS,
	ItemType.Potion,
	ItemType.Poison,
	ItemType.Scroll,
	ItemType.SpellcastingFocus,
	ItemType.AdventuringGear,
	ItemType.Explosive,
	ItemType.Tool,
	ItemType.ArtisansTools,
	ItemType.GamingSet,
	ItemType.Treasure,
	ItemType.TradeBar,
	ItemType.TradeGood,
	ItemType.FoodAndDrink,
	ItemType.Mount,
	ItemType.TackAndHarness,
	ItemType.Vehicle,
	ItemType.Ship,
];

const WEAPON_SUB_TYPES = [ItemSubType.Melee, ItemSubType.Ranged, ItemSubType.Ammunition];
const ARMOR_SUB_TYPES = [ItemSubType.Light, ItemSubType.Medium, ItemSubType.Heavy, ItemSubType.Shield];
const OTHER_SUB_TYPES = [
	ItemSubType.Gemstone,
	ItemSubType.ArtObject,
	ItemSubType.Instrument,
	ItemSubType.Water,
	ItemSubType.Air,
];
const RARITIES = [
	ItemRarity.None,
	ItemRarity.Common,
	ItemRarity.Uncommon,
	ItemRarity.Rare,
	ItemRarity.VeryRare,
	ItemRarity.Legendary,
	ItemRarity.Artifact,
];
const SOURCES = [
	SourceBook.XPHB,
	SourceBook.XDMG,
	SourceBook.AU,
	SourceBook.FRHoF,
	SourceBook.FRAiF,
	SourceBook.PaBTSO,
	SourceBook.ToD,
	SourceBook.DDBD,
	SourceBook.SCAG,
	SourceBook.WDH,
	SourceBook.BGDIA,
];

export const itemFilters = {
	types: enumList('type', BOOK_TYPES),
	weaponSubTypes: enumList('weapon', WEAPON_SUB_TYPES),
	armorSubTypes: enumList('armor', ARMOR_SUB_TYPES),
	otherSubTypes: enumList('kind', OTHER_SUB_TYPES),
	rarities: enumList('rarity', RARITIES),
	reqAttune: flag('attune'),
	curse: flag('curse'),
	sources: enumList('source', SOURCES),
};

export function toItemFilterInput({
	types,
	weaponSubTypes,
	armorSubTypes,
	otherSubTypes,
	...rest
}: FilterValues<typeof itemFilters>): ItemFilterInput {
	const subTypes = [...(weaponSubTypes || []), ...(armorSubTypes || []), ...(otherSubTypes || [])];
	return {
		...rest,
		types: types?.filter((type): type is ItemType => type !== WONDROUS),
		wondrous: types?.includes(WONDROUS) || undefined,
		subTypes: subTypes.length ? subTypes : undefined,
	};
}

const bookTypeToString = (type: ItemType | typeof WONDROUS | null) =>
	type === WONDROUS ? 'Чудесный предмет' : itemTypesToString(type);

const subTypeToString = (subType: ItemSubType | null) => (subType ? ItemSubTypeNames[subType].title : '');

type Props = {
	filters: Filters<typeof itemFilters>;
};

export const ItemFilters: React.FC<Props> = ({filters}) => (
	<FiltersPopup filters={filters} header="Фильтровать предметы">
		<li>
			<ButtonGroup
				items={BOOK_TYPES}
				itemToHtml={bookTypeToString}
				multiple
				label="Тип"
				{...filters.field('types')}
			/>
		</li>
		<li>
			<ButtonGroup
				items={WEAPON_SUB_TYPES}
				itemToHtml={subTypeToString}
				multiple
				label="Оружие"
				{...filters.field('weaponSubTypes')}
			/>
		</li>
		<li>
			<ButtonGroup
				items={ARMOR_SUB_TYPES}
				itemToHtml={subTypeToString}
				multiple
				label="Броня"
				{...filters.field('armorSubTypes')}
			/>
		</li>
		<li>
			<ButtonGroup
				items={OTHER_SUB_TYPES}
				itemToHtml={subTypeToString}
				multiple
				label="Уточнение"
				{...filters.field('otherSubTypes')}
			/>
		</li>
		<li>
			<ButtonGroup
				items={RARITIES}
				itemToHtml={(rarity) => toUpperFirst(itemRarityToString(rarity))}
				multiple
				label="Редкость"
				{...filters.field('rarities')}
			/>
		</li>
		<li>
			<ul className={formCss.formRow}>
				<li>
					<Checkbox label="Требуется настроенность" {...filters.flag('reqAttune')} />
				</li>
				<li>
					<Checkbox label="Проклятый" {...filters.flag('curse')} />
				</li>
			</ul>
		</li>
		<li>
			<ButtonGroup
				items={SOURCES}
				itemToHtml={sourceBookToString}
				multiple
				label="Источник"
				{...filters.field('sources')}
			/>
		</li>
	</FiltersPopup>
);
